import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export interface TeamRole {
  id: string;
  name: string;
  salary: number;
  hourlyRate?: number; // optional hourly rate in USD, used to auto-calculate salary
  headcount: number[]; // 22 months
  category: 'founder' | 'engineering' | 'design' | 'operations' | 'sales' | 'support';
}

export interface OperatingCost {
  id: string;
  name: string;
  description?: string;
  monthlyCosts: number[]; // Dynamic based on planning months
  category: 'infrastructure' | 'facilities' | 'tools' | 'marketing' | 'sales' | 'legal' | 'insurance' | 'professional-services' | 'other';
  frequency: 'monthly' | 'quarterly' | 'yearly' | 'one-time';
  typicalRange?: {
    min: number;
    max: number;
    unit: 'currency' | 'percentage';
  };
  isCustom?: boolean;
  tags?: string[];
  // Extended properties for cost management
  isOneTime?: boolean;
  oneTimeMonth?: number;
  resourceCount?: number;
  customAmount?: number;
}

export interface Shareholder {
  id: string;
  name: string;
  role: string;
  shares: number;
  percentage: number;
}

export interface CompanyProfile {
  name: string;
  industry: string;
  location: string;
  companySize: 'small' | 'medium' | 'large';
  salaryMultiplier: number;
}

export interface StartupTemplate {
  id: string;
  name: string;
  description: string;
  teamRoles: TeamRole[];
  operatingCosts: OperatingCost[];
  companyProfile: CompanyProfile;
}

export type Currency = 'USD' | 'AED' | 'INR';

// Static conversion rates (to be made dynamic later)
// Base currency is INR
export const CURRENCY_RATES: Record<Currency, number> = {
  INR: 1,
  USD: 0.012, // 1 INR = 0.012 USD
  AED: 0.044, // 1 INR = 0.044 AED
};

export type Country = 'India' | 'UAE' | 'USA';

export interface InvestmentFormData {
  investmentAmount: number;
  equityPercentage: number;
  soloPrice: number;
  teamPrice: number;
  unlimitedPrice: number;
  monthlyOpex: number;
  initialUsers: number;
  monthlyGrowthRate: number;
  revenueMultiple: number;
  soloGrowthRate: number;
  teamGrowthRate: number;
  unlimitedGrowthRate: number;
  enableExpenseGrowth: boolean;
  expenseGrowthRate: number;
  expenseGrowthCap: number;
  valuationType: string;
  // Unit Economics
  churnRate: number;
  customerAcquisitionCost: number;
  averageCustomerLifetime: number;
  retentionRate: number;
}

export interface AppState {
  // Project data
  projectName: string;
  description: string;
  industry?: string;

  // Global settings
  currency: Currency;
  country: Country;

  // Investment calculator
  investmentData: InvestmentFormData;
  valuationType: string;

  // Team management
  teamRoles: TeamRole[];
  selectedTemplate?: StartupTemplate;
  planningMonths: number; // Customizable planning period (default 18)

  // Cost analysis
  operatingCosts: OperatingCost[];
  shareholders: Shareholder[];
  expenses: any[];
  projectionMonths: number;
  companyProfile: CompanyProfile;

  // UI state
  costBreakdownEnabled: boolean;
  activeTab: 'overview' | 'calculator' | 'costing' | 'people' | 'packages' | 'unit-economics';
}

interface AppStore extends AppState {
  // Project data actions
  setProjectData: (data: Partial<Pick<AppState, 'projectName' | 'description' | 'industry'>>) => void;

  // Global settings actions
  setCurrency: (currency: Currency) => void;
  setCountry: (country: Country) => void;

  // Investment actions
  setInvestmentData: (data: Partial<InvestmentFormData>) => void;
  setValuationType: (type: string) => void;

  // Team actions
  setTeamRoles: (roles: TeamRole[]) => void;
  addTeamRole: (role: Omit<TeamRole, 'id'>) => void;
  updateTeamRole: (id: string, updates: Partial<TeamRole>) => void;
  deleteTeamRole: (id: string) => void;
  updateHeadcount: (roleId: string, monthIndex: number, value: number) => void;
  resizeTeamHeadcounts: (newPlanningMonths: number) => void;
  setPlanningMonths: (months: number) => void;
  setTemplate: (template: StartupTemplate) => void;
  loadTemplateData: (template: StartupTemplate) => void;

  // Cost actions
  setOperatingCosts: (costs: OperatingCost[]) => void;
  setShareholders: (shareholders: Shareholder[]) => void;
  setExpenses: (expenses: any[]) => void;
  setCompanyProfile: (profile: Partial<CompanyProfile>) => void;
  setCostBreakdownEnabled: (enabled: boolean) => void;

  // Navigation
  setActiveTab: (tab: AppState['activeTab']) => void;

  // Reset
  reset: () => void;
}

// Initial state
const initialState: AppState = {
  projectName: "SaaS Startup",
  description: "",
  industry: "SaaS",
  currency: "INR",
  country: "India",

   investmentData: {
     investmentAmount: 1000000,
     equityPercentage: 10,
     soloPrice: 99,
     teamPrice: 299,
     unlimitedPrice: 999,
     monthlyOpex: 50000,
     initialUsers: 100,
     monthlyGrowthRate: 15,
     revenueMultiple: 8,
     soloGrowthRate: 15,
     teamGrowthRate: 15,
     unlimitedGrowthRate: 15,
     enableExpenseGrowth: false,
     expenseGrowthRate: 10,
      expenseGrowthCap: 200,
      valuationType: "custom",
      // Unit Economics defaults (realistic for new SaaS startups)
      churnRate: 5.0, // 5% monthly churn
      customerAcquisitionCost: 500, // $500 CAC
      averageCustomerLifetime: 24, // 2 years
      retentionRate: 95.0, // 95% monthly retention
   },

  valuationType: "custom",
  teamRoles: [],
   planningMonths: 22, // Default 22 months planning period
  operatingCosts: [],
  shareholders: [],
  expenses: [],
   projectionMonths: 22,
  companyProfile: {
    name: "SaaS Startup",
    industry: "SaaS",
    location: "bangalore",
    companySize: 'small',
    salaryMultiplier: 1.0,
  },

  costBreakdownEnabled: false,
  activeTab: 'overview',
};

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Project data actions
      setProjectData: (data) => set((state) => ({ ...state, ...data })),

      // Global settings actions
      setCurrency: (currency) => set({ currency }),
      setCountry: (country) => set({ country }),

      // Investment actions
      setInvestmentData: (data) =>
        set((state) => ({
          investmentData: { ...state.investmentData, ...data }
        })),
      setValuationType: (type) => set({ valuationType: type }),

      // Team actions
      setTeamRoles: (roles) => set({ teamRoles: roles }),
      addTeamRole: (role) => {
        const newRole: TeamRole = {
          ...role,
          id: Date.now().toString(),
        };
        set((state) => ({
          teamRoles: [...state.teamRoles, newRole]
        }));
      },
      updateTeamRole: (id, updates) =>
        set((state) => ({
          teamRoles: state.teamRoles.map(role =>
            role.id === id ? { ...role, ...updates } : role
          )
        })),
      deleteTeamRole: (id) =>
        set((state) => ({
          teamRoles: state.teamRoles.filter(role => role.id !== id)
        })),
      updateHeadcount: (roleId, monthIndex, value) =>
        set((state) => ({
          teamRoles: state.teamRoles.map(role =>
            role.id === roleId
              ? {
                  ...role,
                  headcount: role.headcount.map((count, index) =>
                    index === monthIndex ? value : count
                  )
                }
              : role
          )
        })),
      setPlanningMonths: (months) => set({ planningMonths: months }),
      resizeTeamHeadcounts: (newPlanningMonths) =>
        set((state) => ({
          teamRoles: state.teamRoles.map(role => ({
            ...role,
            headcount: role.headcount.length < newPlanningMonths
              ? [...role.headcount, ...Array(newPlanningMonths - role.headcount.length).fill(0)]
              : role.headcount.slice(0, newPlanningMonths)
          }))
        })),
      setTemplate: (template) =>
        set({
          selectedTemplate: template,
          teamRoles: template.teamRoles,
          operatingCosts: template.operatingCosts,
          companyProfile: template.companyProfile,
        }),
      loadTemplateData: (template) =>
        set({
          selectedTemplate: template,
          teamRoles: template.teamRoles,
          operatingCosts: template.operatingCosts,
          companyProfile: template.companyProfile,
        }),

      // Cost actions
      setOperatingCosts: (costs) => set({ operatingCosts: costs }),
      setShareholders: (shareholders) => set({ shareholders }),
      setExpenses: (expenses) => set({ expenses }),
      setCompanyProfile: (profile) =>
        set((state) => ({
          companyProfile: { ...state.companyProfile, ...profile }
        })),
      setCostBreakdownEnabled: (enabled) => set({ costBreakdownEnabled: enabled }),

      // Navigation
      setActiveTab: (tab) => set({ activeTab: tab }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: 'saas-calculator-store',
      partialize: (state) => ({
        projectName: state.projectName,
        description: state.description,
        industry: state.industry,
        currency: state.currency,
        country: state.country,
        investmentData: state.investmentData,
        valuationType: state.valuationType,
        teamRoles: state.teamRoles,
        planningMonths: state.planningMonths,
        selectedTemplate: state.selectedTemplate,
        operatingCosts: state.operatingCosts,
        shareholders: state.shareholders,
        expenses: state.expenses,
        companyProfile: state.companyProfile,
        costBreakdownEnabled: state.costBreakdownEnabled,
        activeTab: state.activeTab,
      }),
    }
  )
);

// Selectors for better performance
export const useProjectData = () => {
  const { projectName, description, industry, setProjectData } = useAppStore();
  return {
    projectName,
    description,
    industry,
    setProjectData,
  };
};

export const useInvestmentData = () => {
  const { investmentData, valuationType, setInvestmentData, setValuationType } = useAppStore();
  return {
    data: investmentData,
    valuationType,
    setInvestmentData,
    setValuationType,
  };
};

export const useTeamData = () => {
  const {
    teamRoles,
    addTeamRole,
    updateTeamRole,
    deleteTeamRole,
    updateHeadcount,
    setTeamRoles
  } = useAppStore();
  return {
    teamRoles,
    addTeamRole,
    updateTeamRole,
    deleteTeamRole,
    updateHeadcount,
    setTeamRoles,
  };
};

export const useCostData = () => {
  const {
    operatingCosts,
    shareholders,
    expenses,
    projectionMonths,
    companyProfile,
    costBreakdownEnabled,
    selectedTemplate,
    setOperatingCosts,
    setShareholders,
    setExpenses,
    setCompanyProfile,
    setCostBreakdownEnabled,
    loadTemplateData,
  } = useAppStore();
  return {
    operatingCosts,
    shareholders,
    expenses,
    projectionMonths,
    companyProfile,
    costBreakdownEnabled,
    selectedTemplate,
    setOperatingCosts,
    setShareholders,
    setExpenses,
    setCompanyProfile,
    setCostBreakdownEnabled,
    loadTemplateData,
  };
};

export const useNavigation = () => {
  const { activeTab, setActiveTab } = useAppStore();
  return {
    activeTab,
    setActiveTab,
  };
};